/**
 * Shared color palette for the loading sequence (boot splash → SafeTitleScreen
 * → LoadingRunnerGame → handoff to the real game / landing page).
 *
 * Pinned to the same deep-navy gradient that the landing page and game title
 * screen use (see src/index.css `--background` / `--surface-container-*` and
 * Landing.tsx hero gradient). Keeping these values constant — rather than
 * reading from theme tokens — guarantees the entire load → ready transition
 * looks identical regardless of light/dark mode, and prevents any hue flash
 * while the player is inside the mini endless runner.
 *
 * If you change the landing/title background, update these values to match.
 */
export const LOADING_BG_TOP = "hsl(220, 40%, 11%)"; // surface-container-lowest
export const LOADING_BG_MID = "hsl(220, 35%, 14%)"; // surface-container-low
export const LOADING_BG_BOTTOM = "hsl(220, 40%, 13%)"; // background

/** Full-page gradient identical to the landing page's hero backdrop. */
export const LOADING_PAGE_BG = `linear-gradient(180deg, ${LOADING_BG_TOP} 0%, ${LOADING_BG_MID} 50%, ${LOADING_BG_BOTTOM} 100%)`;

/** Slightly lighter swatch for inset surfaces (progress bar track, mini-game card). */
export const LOADING_SURFACE = "hsl(220, 30%, 17%)"; // surface-container

/** Warm cream foreground that reads on the navy backdrop. */
export const LOADING_FOREGROUND = "hsl(45, 80%, 88%)";
export const LOADING_FOREGROUND_MUTED = "hsla(45, 50%, 80%, 0.78)";

/** Banana-yellow accent (matches `--banana` token used in Landing.tsx). */
export const LOADING_ACCENT = "hsl(48, 96%, 60%)";
export const LOADING_ACCENT_RING = "hsla(48, 96%, 60%, 0.6)";

/** Jungle-floor palette — rich damp earth, leaf litter, mossy edge.
 *  Replaces the earlier slate/stone tones so the runner ground reads as
 *  the actual jungle floor (brown soil, scattered leaves, mossy top line). */
export const LOADING_GROUND_FILL = "hsl(28, 45%, 22%)";        // damp earth base
export const LOADING_GROUND_FILL_DEEP = "hsl(24, 50%, 14%)";   // darker undertone
export const LOADING_GROUND_STRIPE = "hsla(32, 55%, 40%, 0.55)"; // sandy pebbles / litter
export const LOADING_GROUND_LINE = "hsl(95, 45%, 32%)";         // mossy top edge
export const LOADING_GROUND_LEAF = "hsla(95, 55%, 40%, 0.85)";  // scattered leaves
export const LOADING_GROUND_LEAF_ALT = "hsla(45, 70%, 55%, 0.8)"; // amber fallen leaves
export const LOADING_GROUND_GRASS = "hsl(105, 50%, 35%)";       // grass tufts

/** Mini-game backdrop — slightly darker than the page background so the
 *  runner card reads as its own recessed scene against the loading screen.
 *  Pinned (not theme-driven) for the same reason as the page palette. */
export const LOADING_GAME_SKY_TOP = "hsl(220, 45%, 7%)";   // deeper night-jungle top
export const LOADING_GAME_SKY_BOTTOM = "hsl(220, 50%, 5%)"; // even darker near the floor

/** Vine border around the mini-game playfield — thick woody trunk with a
 *  green moss highlight. Reads as the jungle wrapping the gameplay area.
 *
 *  The full ramp is `SHADOW → TRUNK → HIGHLIGHT`. Both the horizontal and
 *  vertical vine gradients share these same three anchors at identical
 *  offsets so the colors line up exactly where they meet at the corner
 *  knots — no residual banding at the joints. */
export const LOADING_VINE_SHADOW = "hsl(28, 40%, 11%)";      // outer-edge shadow
export const LOADING_VINE_TRUNK = "hsl(28, 35%, 18%)";       // dark wet bark
export const LOADING_VINE_HIGHLIGHT = "hsl(95, 45%, 28%)";   // mossy green sheen
export const LOADING_VINE_LEAF = "hsl(110, 50%, 32%)";       // small accent leaves

